import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { adminAPI } from '../../services/api';
import { toast } from 'react-toastify';
import {
  Users,
  UserCheck,
  UserX,
  Clock,
  Building2,
  UserPlus,
  Search,
  Filter,
  Bell,
  Mail,
  Calendar,
  MessageSquare,
  FileText,
  Menu,
  X,
  ClipboardList,
  LogOut
} from 'lucide-react';
import './AdminDashboard.css';
import EmployeeManagement from './EmployeeManagement';
import DepartmentManagement from './DepartmentManagement';
import RoleManagement from './RoleManagement';
import ShiftManagement from './ShiftManagement';
import NotificationManagement from './NotificationManagement';
import HolidayManagement from './HolidayManagement';
import LeaveApprovalView from './LeaveApprovalView';
import AttendanceReport from './AttendanceReport';
import PendingApprovals from './PendingApprovals';
import './NotificationManagement.css';
import './HolidayManagement.css';
import './AttendanceReport.css';


const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // Realtime data endpoints (served from backend; queries local DB directly)
  const REALTIME_PERSONS_API = '/api/php/persons';
  const REALTIME_ATT_SUMMARY_API = '/api/php/attendance-summary';
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'dashboard';
  });
  const [sidebarOpen, setSidebarOpen] = useState(false); // Start closed on mobile
  const [stats, setStats] = useState({
    totalEmployees: 0,
    presentToday: 0,
    absentToday: 0,
    halfday: 0,
    leave: 0,
  });
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(true);
  const [summaryType, setSummaryType] = useState("weekly");
  const [summaryData, setSummaryData] = useState([]);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [attendanceFilterType, setAttendanceFilterType] = useState('present'); // 'present', 'absent', 'leave'
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [attendanceModalPage, setAttendanceModalPage] = useState(1);
  const [allEmployees, setAllEmployees] = useState([]);
  const [todayAttendance, setTodayAttendance] = useState([]);
  const [organizationValidity, setOrganizationValidity] = useState(null);

  // Close sidebar on route/page change
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Fetch organization validity information
  const fetchOrganizationValidity = async () => {
    try {
      // Get current organization
      const currentOrgRes = await fetch('/api/organizations/current');
      if (!currentOrgRes.ok) return;
      
      const currentOrgData = await currentOrgRes.json();
      const currentOrg = currentOrgData?.organization;
      
      if (!currentOrg?.code) {
        console.log('No organization selected');
        return;
      }

      // Fetch validity information
      const validityRes = await fetch(`/api/organizations/validity/${currentOrg.code}`);
      if (validityRes.ok) {
        const validityData = await validityRes.json();
        if (validityData.success) {
          setOrganizationValidity(validityData.data);
          console.log('📅 Organization validity:', validityData.data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch organization validity:', error);
    }
  };

  // Auto-refresh data every 5 minutes
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      console.log('🔄 Auto-refreshing data...');
      fetchDashboardData();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(refreshInterval);
  }, []);

  useEffect(() => {
    fetchSummary("weekly");
  }, []);

  useEffect(() => {
    fetchDashboardData();
    fetchOrganizationValidity();
  }, []);


const fetchSummary = async (type) => {
  try {
    const res = await adminAPI.getAttendanceSummary(type);
    if (res.data.success) {
      setSummaryData(res.data.data);
    }
  } catch (err) {
    console.error(err);
  }
};



  const fetchDashboardData = async () => {
    // Only use PHP persons API for My Contacts
    let persons = [];
    try {
      const resp = await fetch(REALTIME_PERSONS_API);
      if (!resp.ok) throw new Error(`HTTP error! status: ${resp.status}`);
      const json = await resp.json();
      console.log('Raw Realtime DB response:', json);  // Debug log

      // Handle different response formats
      if (json && json.data && Array.isArray(json.data)) {
        // If response is wrapped in {data: [...]}
        persons = json.data;
      } else if (Array.isArray(json)) {
        // If response is direct array
        persons = json;
      } else {
        console.error('Unexpected API response format:', json);
        toast.error('Invalid data format received from contacts API');
      }
      console.log('Processed persons data:', persons);
    } catch (phpErr) {
      console.error('Failed to load PHP persons API:', phpErr);
      // toast.error('Failed to load contacts data: ' + phpErr.message);
    }

    try {
      console.log('🔵 Fetching dashboard data from backend...');
      // Get today's date for attendance filter
      const todayDate = new Date().toISOString().split('T')[0];
      
      const [statsRes, deptRes, rolesRes, employeesRes, pendingRes, attendanceRes] = await Promise.all([
        adminAPI.getDashboardStats().catch(e => {
          console.error('❌ getDashboardStats failed:', e);
          return { data: {} };
        }),
        adminAPI.getDepartments().catch(e => {
          console.error('❌ getDepartments failed:', e);
          return { data: [] };
        }),
        adminAPI.getRoles().catch(e => {
          console.error('❌ getRoles failed:', e);
          return { data: [] };
        }),
        adminAPI.getEmployees().catch(e => {
          console.error('❌ getEmployees failed:', e);
          return { data: [] };
        }),
        adminAPI.getPendingPersons().catch(e => {
          console.error('❌ getPendingPersons failed:', e);
          return { data: [] };
        }),
        adminAPI.getAttendance({ date: todayDate }).catch(e => {
          console.error('❌ getAttendance failed:', e);
          return { data: [] };
        })
      ]);
      console.log('✅ Backend data fetched:', { stats: statsRes.data, depts: deptRes.data?.length, roles: rolesRes.data?.length, employees: employeesRes.data?.length, pending: pendingRes.data?.length, attendance: attendanceRes.data?.length });



      // Only use PHP persons data for My Contacts preview. Also fetch employees
      // from the admin API to compute status-based counts (active/pending/inactive).
      const baseStats = statsRes.data || {};
      const personsCount = persons.length; // Only use PHP persons count for contact list

      const employees = (employeesRes && employeesRes.data) || [];
      // Compute status counts. Prefer string `status` when present, otherwise use statusflag.
      // ---- STEP 1: Employees table status counting ----
      let activeCount = 0;
      let pendingCount = 0;
      let inactiveCount = 0;

      employees.forEach(emp => {
        const statusText = emp.status ? emp.status.toLowerCase() : null;

        if (statusText === "active") {
          activeCount++;
          return;
        }

        if (statusText === "pending") {
          pendingCount++;
          return;
        }

        if (statusText === "inactive") {
          inactiveCount++;
          return;
        }

        // fallback using statusflag ONLY when status column missing
        if (!statusText) {
          if (emp.statusflag === 1) activeCount++;
          else if (emp.statusflag === 0) pendingCount++;
        }
      });

      // ---- STEP 2: Count persons with ID > 100 (face-registered persons) ----
      const registeredPersonsCount = persons.filter(p => {
        const pid = p.id || p.person_id;
        return pid && pid > 100; // Face-registered persons have high IDs
      }).length;

      // ---- STEP 3: My Contacts should reflect active employees only ----
      const totalContacts = activeCount;
      
      // Get actual pending count directly from backend API
      const actualPendingCount = pendingRes.data?.length || 0;
      const combinedPending = actualPendingCount + pendingCount;
      
      console.log('🔢 Pending persons from backend API:', actualPendingCount);
      console.log('🔢 Pending employees from employees table:', pendingCount);
      console.log('🔢 Combined pending count:', combinedPending);
      
      console.log('📊 Dashboard Counts:', {
        personsFromPHP: persons.length,
        personsData: persons.slice(0, 3), // Show first 3 persons for debugging
        registeredPersons: registeredPersonsCount,
        activeEmployees: activeCount,
        pendingEmployees: pendingCount,
        actualPendingCount: actualPendingCount,
        combinedPending: combinedPending,
        inactiveEmployees: inactiveCount,
        totalContacts: totalContacts
      });

      // Prepare recent attendance preview (first 10 persons)
      let preview = persons.slice(0, 10).map((p, idx) => ({
        sno: p.id || p.enroll_id || idx + 1,
        employee_name: p.name || p.fullname || `Person ${idx + 1}`,
        departmentname: p.roll_id === '0' ? 'Staff' : 'Other',
        attendance_status: '',
        time_status: '',
        check_in_time: '',
        device_accessed: ''
      }));

      // Try to fetch attendance summary for TODAY from the realtime DB (tolerant)
      const today = new Date().toISOString().split('T')[0];
      let presentToday = baseStats.presentToday || 0;
      let absentToday = baseStats.absentToday || 0;
      let leaveToday = baseStats.leave || 0;
      
      // Use backend stats directly - they already query attendance_summary correctly
      // Only fetch preview data from PHP proxy if needed
      try {
        // Use backend proxy to avoid CORS issues. Backend will query the attendance_summary table for today's records.
        const attResp = await fetch(`/api/php/attendance-summary?date=${today}`);
        if (attResp.ok) {
          const attJson = await attResp.json();

          // Normalize to an array of attendance records for preview only
          let todays = [];
          if (attJson && typeof attJson === 'object' && !Array.isArray(attJson)) {
            // Support wrapper formats { data: [...] } or { records: [...] }
            if (Array.isArray(attJson.data)) todays = attJson.data;
            else if (Array.isArray(attJson.records)) todays = attJson.records;
            else if (attJson.success && Array.isArray(attJson.data)) todays = attJson.data;
          } else if (Array.isArray(attJson)) {
            todays = attJson;
          }

          // Use PHP data ONLY for preview, NOT for stats
          if (Array.isArray(todays) && todays.length > 0) {
            const previewFromAtt = todays.slice(0, 10).map((r, idx) => ({
              sno: idx + 1,
              employee_name: r.name || r.employee_name || `ID ${r.enroll_id || r.id || 'N/A'}`,
              departmentname: r.department || '',
              attendance_status: r.attendance_status || r.status || '',
              time_status: r.time_status || r.late_status || '',
              check_in_time: r.first_in || r.clock_in_time || '',
              device_accessed: r.device || ''
            }));

            // Replace preview with today's attendance preview when available
            if (previewFromAtt.length > 0) preview = previewFromAtt;
          }
        } else {
          console.warn('Attendance proxy responded with status', attResp.status);
        }
      } catch (attErr) {
        console.warn('Failed to fetch PHP attendance summary:', attErr.message);
      }

      setStats({
        totalEmployees: totalContacts, // Show active employees only
        presentToday,
        absentToday,
        leave: baseStats.leave || 0,
        lateToday: baseStats.lateToday || 0,
        recentAttendance: preview,
        contactsByStatus: {
          active: activeCount,
          pending: actualPendingCount, // Show employees with pending status + unregistered persons
          inactive: inactiveCount
        }
      });

      // Create enriched employee list with attendance data
      const attendanceData = attendanceRes.data || [];
      setTodayAttendance(attendanceData);
      
      // Merge employee data with attendance data
      const enrichedEmployees = employees.map(emp => {
        const attData = attendanceData.find(att => att.employee_id === emp.id || att.employeeId === emp.id);
        return {
          ...emp,
          attendanceStatus: attData?.attendance_status || attData?.status || 'absent',
          checkInTime: attData?.check_in_time,
          checkOutTime: attData?.check_out_time
        };
      });
      
      console.log('📊 Enriched employees with attendance:', enrichedEmployees.slice(0, 3));
      setAllEmployees(enrichedEmployees);

      setDepartments(deptRes.data || []);
      setRoles(rolesRes.data || []);
      console.log('✅ Dashboard data loaded successfully');
    } catch (error) {
      console.error('❌ Dashboard data error:', error);
      toast.error('Failed to fetch dashboard data: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
      // varun
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
  };

  const handleAttendanceBoxClick = (filterType) => {
    setAttendanceFilterType(filterType);
    setAttendanceModalPage(1);
    
    // Fetch filtered attendance from backend
    const today = new Date().toISOString().split('T')[0];
    adminAPI.getAttendance({ date: today, filterType: filterType, limit: 200 })
      .then(response => {
        console.log(`📊 Retrieved ${response.data?.length || 0} employees for filter: ${filterType}`, response.data);
        setFilteredEmployees(response.data || []);
        setAttendanceModalPage(1);
        setShowAttendanceModal(true);
      })
      .catch(error => {
        console.error(`❌ Failed to fetch ${filterType} employees:`, error);
        toast.error(`Failed to fetch ${filterType} employees`);
      });
  };

  const handleCloseAttendanceModal = () => {
    setShowAttendanceModal(false);
    setFilteredEmployees([]);
    setAttendanceModalPage(1);
  };

  const attendanceModalItemsPerPage = 10;
  const attendanceModalTotalPages = Math.max(1, Math.ceil(filteredEmployees.length / attendanceModalItemsPerPage));
  const attendanceModalStartIndex = (attendanceModalPage - 1) * attendanceModalItemsPerPage;
  const attendanceModalEndIndex = attendanceModalStartIndex + attendanceModalItemsPerPage;
  const paginatedFilteredEmployees = filteredEmployees.slice(attendanceModalStartIndex, attendanceModalEndIndex);
  const attendanceModalMaxPageButtons = 5;
  const attendanceModalPageWindowStart = Math.max(
    1,
    Math.min(
      attendanceModalPage - Math.floor(attendanceModalMaxPageButtons / 2),
      attendanceModalTotalPages - attendanceModalMaxPageButtons + 1
    )
  );
  const attendanceModalPageWindowEnd = Math.min(
    attendanceModalTotalPages,
    attendanceModalPageWindowStart + attendanceModalMaxPageButtons - 1
  );
  const attendanceModalVisiblePages = Array.from(
    { length: attendanceModalPageWindowEnd - attendanceModalPageWindowStart + 1 },
    (_, index) => attendanceModalPageWindowStart + index
  );

  useEffect(() => {
    if (attendanceModalPage > attendanceModalTotalPages) {
      setAttendanceModalPage(1);
    }
  }, [attendanceModalPage, attendanceModalTotalPages]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate(`/admin/dashboard?tab=${tab}`, { replace: true });
    closeSidebar();
  };

  // keep state in sync if the user manually navigates to a tabbed URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabFromUrl = params.get('tab');
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [location.search, activeTab]);

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return renderDashboard();
      case 'employees':
        return <EmployeeManagement />;
      case 'pending-approvals':
        return <PendingApprovals key={Date.now()} />;
      case 'departments':
        return <DepartmentManagement />;
      case 'roles':
        return <RoleManagement />;
      case 'shifts':
        return <ShiftManagement />;
      case 'notifications':
        return <NotificationManagement />;
      case 'holidays':
        return <HolidayManagement />;
      case 'attendance-report':
        return <AttendanceReport />;
      case 'leave-approvals':
        return <LeaveApprovalView />;
      default:
        return renderDashboard();
    }
  };

  const footerTextMap = {
    dashboard: 'Dashboard Overview',
    employees: 'Employee Management',
    'pending-approvals': 'Pending Approvals',
    departments: 'Department Management',
    roles: 'Role Management',
    shifts: 'Shift Management',
    notifications: 'Notification Management',
    holidays: 'Holiday Management',
    'attendance-report': 'Attendance Report',
    'leave-approvals': 'Leave Approvals',
  };

  const currentFooterText = footerTextMap[activeTab] || 'Dashboard Overview';

  const renderDashboard = () => (
    <div className="dashboard-content">
      <div className="dashboard-header">
        <div className="header-left">
          <h2>DASHBOARD OVERVIEW</h2>
          <p>Welcome back, {user?.name}</p>
        </div>
        <div className="header-right">
          <div className="subscription-title">
            Validity till {organizationValidity?.expiry_date
              ? new Date(organizationValidity.expiry_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
              : 'N/A'}
          </div>
          <div className="subscription-meta">
            {organizationValidity?.remaining_days ?? 365} days remaining
          </div>
          <div className="subscription-status">
            {organizationValidity?.is_expired ? 'Expired' : 'My Subscription'}
          </div>
        </div>
      </div>

      {/* Top Stats Cards */}
      <div className="top-stats-grid">
        <div
          className="stat-card contacts"
          onClick={() => handleTabChange('employees')}
          style={{ cursor: 'pointer' }}
        >
          <div className="stat-icon">
            <Users size={32} />
          </div>
          <div className="stat-content">
            <h3>{stats.totalEmployees}</h3>
            <p>My Contacts</p>
          </div>
        </div>

        <div
          className="stat-card groups"
          onClick={() => handleTabChange('pending-approvals')}
          style={{ cursor: 'pointer' }}
        >
          <div className="stat-icon">
            <UserPlus size={32} />
          </div>
          <div className="stat-content">
            <h3>{stats.contactsByStatus?.pending ?? 0}</h3>
            <p>Pending Approvals</p>
          </div>
        </div>

      </div>
      {/* Middle Section with Attendance and Staff Info */}
      <div className="middle-section">
        <div className="attendance-card">
          <div className="attendance-header">
            <div className="tabs">
              <div className="tab active">Today's Attendance</div>
             
            </div>
            <div className="total-count">*Total Employees: {stats.totalEmployees + stats.contactsByStatus?.pending || 0}</div>
          </div>
          <div className="attendance-stats">
            <div className="stat-box present" onClick={() => handleAttendanceBoxClick('present')} style={{ cursor: 'pointer' }}>
              <span className="count">{stats.presentToday}</span>
              <span className="label">Today's Attendance</span>
            </div>
            <div className="stat-box leave" onClick={() => handleAttendanceBoxClick('leave')} style={{ cursor: 'pointer' }}>
              <span className="count">{stats.leave || 0}</span>
              <span className="label">Leaves</span>
            </div>
            <div className="stat-box absent" onClick={() => handleAttendanceBoxClick('absent')} style={{ cursor: 'pointer' }}>
              <span className="count">{stats.absentToday}</span>
              <span className="label">Total Absence</span>
            </div>
          </div>
        </div>
      </div>

    <div className="recent-attendance">
      <h3>ATTENDANCE SUMMARY</h3>

      {/* Dropdown */}
      <select
        value={summaryType}
        onChange={(e) => {
          setSummaryType(e.target.value);
          fetchSummary(e.target.value);
        }}
        className='select-dropdown'
      >
        <option value="weekly">Weekly</option>
        <option value="monthly">Monthly</option>
      </select>

      <div className="attendance-table">
        <table>
          <thead>
            <tr>
              <th>Period</th>
              <th>Present</th>
              <th>Absent</th>
              <th>Halfday</th>
            </tr>
          </thead>
          <tbody>
            {summaryData.map((row, index) => (
              <tr key={index}>
                <td>{row.period}</td>
                <td><span className="mobile-label">{row.present}</span><span className="mobile-text"> Present</span></td>
                <td><span className="mobile-label">{row.absent}</span><span className="mobile-text"> Absent</span></td>
                <td><span className="mobile-label">{row.halfday}</span><span className="mobile-text"> Halfday</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    </div>
  );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
  <div 
    className={`admin-dashboard 
      ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'} 
      ${collapsed ? 'collapsed' : ''}`}
  >
    {/* Hamburger - mobile only */}
    <button className="mobile-menu-toggle" onClick={toggleSidebar}>
      {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
    </button>

    {/* Logout button - mobile only (top right) */}
    <button 
      className="mobile-logout-btn" 
      onClick={() => {
        logout();
        navigate('/login');
      }}
      title="Logout"
    >
      <LogOut size={24} />
    </button>

    <div className="mobile-header-title">ACSFaScan- Facial Recognition </div>

 


    {/* Sidebar Overlay - only mobile */}
    {sidebarOpen && <div className="sidebar-overlay" onClick={closeSidebar}></div>}

    <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
     
     <div 
  className="sidebar-header logo-area"
  onClick={() => {
    // Collapse toggles only on desktop
    if (window.innerWidth > 768) setCollapsed(!collapsed);
  }}
  style={{ cursor: "pointer", display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
>
  <div className="logo-wrapper" style={{ flex: 1 }}>
    {/* If you have a logo image */}
    {/* <img src={logo} alt="Admin Panel" className="admin-logo" /> */}

    {/* Fallback: Text Logo */}
    <div className="logo-circle"><Menu size={20} /></div>

    {!collapsed && (
      <div className="logo-text">
        <h2>ADMIN PANEL</h2>
        <p>Management</p>
      </div>
    )}
  </div>

  {/* Close button for mobile */}
  <button 
    className="sidebar-close-btn"
    onClick={(e) => {
      e.stopPropagation();
      closeSidebar();
    }}
    style={{
      display: window.innerWidth <= 480 ? 'flex' : 'none',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'none',
      border: 'none',
      color: '#0b3c5d',
      cursor: 'pointer',
      padding: '8px',
      flexShrink: 0
    }}
    title="Close menu"
  >
    <X size={24} />
  </button>
</div>


      <nav className="sidebar-nav">
        <ul>
          <li>
            <button 
              className={activeTab === 'dashboard' ? 'active' : ''} 
              onClick={() => handleTabChange('dashboard')}
              title="Dashboard"
            >
              <Building2 size={20} />
              {!collapsed && <span>Dashboard</span>}
            </button>
          </li>

          <li>
            <button 
              className={activeTab === 'employees' ? 'active' : ''} 
              onClick={() => handleTabChange('employees')}
              title="Employees"
            >
              <Users size={20} />
              {!collapsed && <span>Employees</span>}
            </button>
          </li>

          <li>
            <button 
              className={activeTab === 'pending-approvals' ? 'active' : ''} 
              onClick={() => handleTabChange('pending-approvals')}
              title="Pending Approvals"
            >
              <UserCheck size={20} />
              {!collapsed && <span>Pending Approvals</span>}
            </button>
          </li>

          <li>
            <button 
              className={activeTab === 'departments' ? 'active' : ''} 
              onClick={() => handleTabChange('departments')}
              title="Departments"
            >
              <Building2 size={20} />
              {!collapsed && <span>Departments</span>}
            </button>
          </li>

          <li>
            <button 
              className={activeTab === 'roles' ? 'active' : ''} 
              onClick={() => handleTabChange('roles')}
              title="Roles"
            >
              <UserPlus size={20} />
              {!collapsed && <span>Roles</span>}
            </button>
          </li>

          <li>
            <button 
              className={activeTab === 'shifts' ? 'active' : ''} 
              onClick={() => handleTabChange('shifts')}
              title="Shifts"
            >
              <Clock size={20} />
              {!collapsed && <span>Define Shift</span>}
            </button>
          </li>

          <li>
            <button 
              className={activeTab === 'notifications' ? 'active' : ''} 
              onClick={() => handleTabChange('notifications')}
              title="Notifications"
            >
              <Bell size={20} />
              {!collapsed && <span>Notifications</span>}
            </button>
          </li>

          <li>
            <button 
              className={activeTab === 'holidays' ? 'active' : ''} 
              onClick={() => handleTabChange('holidays')}
              title="Holidays"
            >
              <Calendar size={20} />
              {!collapsed && <span>Holidays</span>}
            </button>
          </li>

          <li>
            <button 
              className={activeTab === 'leave-approvals' ? 'active' : ''} 
              onClick={() => handleTabChange('leave-approvals')}
              title="Leave Approvals"
            >
              <ClipboardList size={20} />
              {!collapsed && <span>Leave Approvals</span>}
            </button>
          </li>

          <li>
            <button 
              className={activeTab === 'attendance-report' ? 'active' : ''} 
              onClick={() => handleTabChange('attendance-report')}
              title="Attendance Report"
            >
              <FileText size={20} />
              {!collapsed && <span>Attendance Report</span>}
            </button>
          </li>
        </ul>
      </nav>

      <div className="sidebar-footer">
        {!collapsed && (
          <>
            <div className="user-info">
              <p>{user?.name}</p>
              <small>{user?.email}</small>
            </div>
            <button className="logout-btn" onClick={handleLogout}>
              <LogOut size={16} />
              Logout
            </button>
          </>
        )}
      </div>
    </div>

    <div className="admin-top-header">
      <h1 className="admin-top-header-title">ACSFaScan- FACIAL RECOGNITION AND ATTEDENCE SYSTEM</h1>
    </div>

    <div className={`main-content ${activeTab === 'dashboard' ? 'dashboard-main-content' : ''} ${activeTab === 'employees' ? 'employees-main-content' : ''} ${activeTab === 'notifications' ? 'notifications-main-content' : ''} ${activeTab === 'attendance-report' ? 'attendance-report-main-content' : ''}`}>{renderContent()}</div>

    <div className="admin-fixed-footer">{currentFooterText}</div>

    {/* Attendance Modal */}
    {showAttendanceModal && (
      <div className="attendance-modal-overlay" onClick={handleCloseAttendanceModal}>
        <div className="attendance-modal" onClick={(e) => e.stopPropagation()}>
          <div className="attendance-modal-header">
            <h3>
              {attendanceFilterType === 'present' && 'Employees Present Today'}
              {attendanceFilterType === 'absent' && 'Absent Employees'}
              {attendanceFilterType === 'leave' && 'Employees on Leave'}
            </h3>
            <button 
              onClick={handleCloseAttendanceModal}
              style={{
                background: 'none',
                border: 'none',
                color: '#ffffff',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '0'
              }}
            >
              ×
            </button>
          </div>
          <div className="attendance-modal-body">
            {filteredEmployees.length > 0 ? (
              <>
                <div className="attendance-modal-table-wrap">
                  <table className="attendance-modal-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #ddd' }}>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Name</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Email</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Department</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Role</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedFilteredEmployees.map((emp, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '12px' }}>{emp.employee_name || emp.name || 'N/A'}</td>
                        <td style={{ padding: '12px' }}>{emp.email || 'N/A'}</td>
                        <td style={{ padding: '12px' }}>{emp.departmentname || emp.department || 'N/A'}</td>
                        <td style={{ padding: '12px' }}>{emp.rolename || emp.role || 'N/A'}</td>
                        <td style={{ padding: '12px', fontWeight: 'bold' }}>
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '4px',
                            backgroundColor: (emp.attendance_status || emp.attendanceStatus || '').toLowerCase() === 'present' ? '#d4edda' : (emp.attendance_status || emp.attendanceStatus || '').toLowerCase() === 'absent' ? '#f8d7da' : '#e7f3ff',
                            color: (emp.attendance_status || emp.attendanceStatus || '').toLowerCase() === 'present' ? '#155724' : (emp.attendance_status || emp.attendanceStatus || '').toLowerCase() === 'absent' ? '#721c24' : '#004085'
                          }}>
                            {(emp.attendance_status || emp.attendanceStatus || '').toLowerCase() === 'present' ? 'Present' : (emp.attendance_status || emp.attendanceStatus || '').toLowerCase() === 'absent' ? 'Absent' : (emp.attendance_status || emp.attendanceStatus || '').toLowerCase() === 'leave' || (emp.attendance_status || emp.attendanceStatus || '').toLowerCase() === 'on leave' ? 'Leave' : (emp.attendance_status || emp.attendanceStatus || 'N/A')}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  </table>
                </div>

                {filteredEmployees.length > attendanceModalItemsPerPage && (
                  <div className="attendance-modal-pagination">
                    <button
                      className="attendance-modal-page-btn"
                      onClick={() => setAttendanceModalPage((prev) => Math.max(1, prev - 1))}
                      disabled={attendanceModalPage === 1}
                    >
                      Prev
                    </button>

                    <div className="attendance-modal-page-numbers">
                      {attendanceModalVisiblePages.map((page) => (
                        <button
                          key={page}
                          className={`attendance-modal-page-btn ${attendanceModalPage === page ? 'active' : ''}`}
                          onClick={() => setAttendanceModalPage(page)}
                        >
                          {page}
                        </button>
                      ))}
                    </div>

                    <button
                      className="attendance-modal-page-btn"
                      onClick={() => setAttendanceModalPage((prev) => Math.min(attendanceModalTotalPages, prev + 1))}
                      disabled={attendanceModalPage === attendanceModalTotalPages}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                <p>No employees found in this category.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    )}
  </div>
);
};

export default AdminDashboard;